import { randomUniqueId } from "$routes/_lib/random-id";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { useRxData } from "rxdb-hooks";
import type { EpocusReferral } from "../../../../global-providers/rxdb-provider/collections/epocus-referral";
import {
  RXDB_EPOCUS_REFERRAL_COLLECTION_NAME,
  useRxCollectionEpocusReferral,
} from "../../../../global-providers/rxdb-provider/collections/epocus-referral";
import type {
  EpocusReferralActionTaken,
  EpocusReferralDiagnosisConfirmed,
  EpocusReferralOutcome,
} from "../../../../global-providers/rxdb-provider/collections/epocus-referral-outcome";
import {
  RXDB_EPOCUS_REFERRAL_OUTCOME_COLLECTION_NAME,
  useRxCollectionEpocusReferralOutcome,
} from "../../../../global-providers/rxdb-provider/collections/epocus-referral-outcome";
import {
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_RED,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../_lib/epocus-palette";
import { epocusTheme } from "../../_lib/epocus-theme-provider";
import { useDemoViewer } from "../../_lib/use-demo-viewer";
import { linkToEpocusReferralsInbox } from "../../links";
import { HARDCODED_RECEIVERS } from "../_lib/hardcoded-receivers";
import { ReferralStatusChip } from "../_lib/referral-status-chip";
import { ReferralPrint } from "./referral-print";

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 680,
  margin: "0 auto",
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
  },
});

const Section = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 12,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const FieldLabel = styled(Typography)({
  fontSize: rem(11),
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: EPOCUS_TEXT_SECONDARY,
  marginBottom: 2,
});

const FieldValue = styled(Typography)({
  fontSize: rem(15),
  color: EPOCUS_TEXT_PRIMARY,
});

const URGENCY_LABELS: Record<string, string> = {
  routine: "Routine",
  urgent: "Urgent",
  emergency: "Emergency",
};

const DIAGNOSIS_OPTIONS: {
  value: EpocusReferralDiagnosisConfirmed;
  label: string;
}[] = [
  { value: "yes", label: "Yes — diagnosis confirmed" },
  { value: "no", label: "No — not confirmed" },
  { value: "unable", label: "Unable to determine" },
];

const ACTION_OPTIONS: { value: EpocusReferralActionTaken; label: string }[] = [
  { value: "admitted", label: "Admitted" },
  { value: "treatedDischarged", label: "Treated and discharged" },
  { value: "referredOnward", label: "Referred onward" },
  { value: "noAction", label: "No action required" },
];

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export const EpocusReferralDetailPage: FC = () => {
  const { referralId } = useParams<{ referralId: string }>();
  const navigate = useNavigate();
  const viewerId = useDemoViewer();
  const referralCollection = useRxCollectionEpocusReferral();
  const outcomeCollection = useRxCollectionEpocusReferralOutcome();

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const printRef = useRef<HTMLDivElement>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => setIsPrinting(false),
    documentTitle: `ePocus-Referral-${referralId ?? ""}`,
  });

  // Outcome form state
  const [dateSeen, setDateSeen] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [diagnosisConfirmed, setDiagnosisConfirmed] =
    useState<EpocusReferralDiagnosisConfirmed>("yes");
  const [actionTaken, setActionTaken] =
    useState<EpocusReferralActionTaken>("treatedDischarged");
  const [clinicalNotes, setClinicalNotes] = useState("");

  const { result: referrals, isFetching } = useRxData<EpocusReferral>(
    RXDB_EPOCUS_REFERRAL_COLLECTION_NAME,
    (col) => col?.findOne(referralId ?? ""),
  );
  const referral = referrals?.[0] ?? null;

  const { result: outcomes } = useRxData<EpocusReferralOutcome>(
    RXDB_EPOCUS_REFERRAL_OUTCOME_COLLECTION_NAME,
    (col) =>
      col?.findOne({ selector: { referralId: { $eq: referralId ?? "" } } }),
  );
  const outcome = outcomes?.[0] ?? null;

  // Auto-acknowledge when the receiving user first opens this page
  useEffect(() => {
    if (
      referral &&
      referralCollection &&
      referral.status === "sent" &&
      viewerId === referral.receivingUserId
    ) {
      referralCollection
        .findOne(referral.id)
        .exec()
        .then((doc) =>
          doc?.patch({
            status: "acknowledged",
            acknowledgedAt: new Date().toISOString(),
          }),
        )
        .catch(() => {
          // Non-critical — auto-acknowledge can be retried on next open
        });
    }
  }, [referral, referralCollection, viewerId]);

  useEffect(() => {
    if (isPrinting) handlePrint();
  }, [isPrinting, handlePrint]);

  const handleSubmitOutcome = async () => {
    if (!outcomeCollection || !referralCollection || !referral) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const now = new Date().toISOString();
      const outcomeDoc: EpocusReferralOutcome = {
        id: randomUniqueId(),
        referralId: referral.id,
        recordedByUserId: viewerId ?? "",
        patientSeen: true,
        dateSeen,
        diagnosisConfirmed,
        actionTaken,
        clinicalNotes: clinicalNotes.trim() || null,
        createdAt: now,
      };
      await outcomeCollection.insert(outcomeDoc);
      const doc = await referralCollection.findOne(referral.id).exec();
      await doc?.patch({ status: "outcomeRecorded", outcomeRecordedAt: now });
    } catch {
      setSubmitError("Failed to submit outcome. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (isFetching) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!referral) {
    return (
      <PageRoot>
        <Typography>Referral not found.</Typography>
      </PageRoot>
    );
  }

  const sender = HARDCODED_RECEIVERS.find(
    (r) => r.id === referral.referringUserId,
  );
  const receiver = HARDCODED_RECEIVERS.find(
    (r) => r.id === referral.receivingUserId,
  );
  const isReceiver = viewerId === referral.receivingUserId;

  return (
    <PageRoot>
      <Button
        onClick={() => navigate(linkToEpocusReferralsInbox())}
        style={{
          color: EPOCUS_PRIMARY,
          textTransform: "none",
          fontSize: rem(14),
          padding: "4px 0",
          minWidth: 0,
          alignSelf: "flex-start",
        }}
      >
        ‹ Back to inbox
      </Button>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        style={{ gap: 8 }}
      >
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography
            variant="h5"
            style={{
              fontWeight: 800,
              fontSize: rem(22),
              color: EPOCUS_TEXT_PRIMARY,
            }}
          >
            Referral
          </Typography>
          <ReferralStatusChip status={referral.status} />
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setIsPrinting(true)}
          style={{
            textTransform: "none",
            fontSize: rem(13),
            fontWeight: 600,
            borderColor: EPOCUS_BORDER,
            color: EPOCUS_TEXT_SECONDARY,
            borderRadius: 8,
            padding: "5px 12px",
          }}
        >
          Export PDF
        </Button>
      </Box>

      {/* Referral summary */}
      <Section>
        <Box>
          <FieldLabel>Urgency</FieldLabel>
          <FieldValue style={{ fontWeight: 700 }}>
            {URGENCY_LABELS[referral.urgency]}
          </FieldValue>
        </Box>
        <Box>
          <FieldLabel>Clinical Reason</FieldLabel>
          <FieldValue>{referral.clinicalReason}</FieldValue>
        </Box>
        <Box>
          <FieldLabel>Referring Clinician</FieldLabel>
          <FieldValue>
            {sender
              ? `${sender.name} · ${sender.role}`
              : referral.referringUserId}
          </FieldValue>
        </Box>
        <Box>
          <FieldLabel>Receiving Clinician</FieldLabel>
          <FieldValue>
            {receiver
              ? `${receiver.name} · ${receiver.role}, ${receiver.facility}`
              : referral.receivingUserId}
          </FieldValue>
        </Box>
        <Box>
          <FieldLabel>Sent</FieldLabel>
          <FieldValue>{formatDate(referral.sentAt)}</FieldValue>
        </Box>
        {referral.acknowledgedAt && (
          <Box>
            <FieldLabel>Acknowledged</FieldLabel>
            <FieldValue>{formatDate(referral.acknowledgedAt)}</FieldValue>
          </Box>
        )}
      </Section>

      {/* Outcome form — shown to receiver when status is acknowledged */}
      {isReceiver && referral.status === "acknowledged" && !outcome && (
        <Section>
          <Typography
            style={{
              fontWeight: 700,
              fontSize: rem(16),
              color: EPOCUS_TEXT_PRIMARY,
            }}
          >
            Submit Outcome
          </Typography>

          <Box>
            <FieldLabel>Date Seen</FieldLabel>
            <TextField
              type="date"
              value={dateSeen}
              onChange={(e) => setDateSeen(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{ style: { fontSize: rem(16) } }}
            />
          </Box>

          <Box>
            <FieldLabel>Diagnosis Confirmed</FieldLabel>
            <Select
              value={diagnosisConfirmed}
              onChange={(e) =>
                setDiagnosisConfirmed(
                  e.target.value as EpocusReferralDiagnosisConfirmed,
                )
              }
              variant="outlined"
              fullWidth
              style={{ fontSize: rem(16) }}
            >
              {DIAGNOSIS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <FieldLabel>Action Taken</FieldLabel>
            <Select
              value={actionTaken}
              onChange={(e) =>
                setActionTaken(e.target.value as EpocusReferralActionTaken)
              }
              variant="outlined"
              fullWidth
              style={{ fontSize: rem(16) }}
            >
              {ACTION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <FieldLabel>Clinical Notes (optional)</FieldLabel>
            <TextField
              multiline
              rows={3}
              value={clinicalNotes}
              onChange={(e) => setClinicalNotes(e.target.value)}
              variant="outlined"
              placeholder="Any additional clinical observations or recommendations..."
              fullWidth
              inputProps={{ style: { fontSize: rem(16) } }}
            />
          </Box>

          {submitError && (
            <Typography
              style={{ color: EPOCUS_RED, fontSize: rem(13), fontWeight: 600 }}
            >
              {submitError}
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitOutcome}
            disabled={submitting}
            style={{
              borderRadius: 10,
              padding: "12px 24px",
              fontWeight: 700,
              fontSize: rem(15),
              textTransform: "none",
              minHeight: 48,
            }}
            fullWidth
          >
            {submitting ? "Submitting…" : "Submit Outcome"}
          </Button>
        </Section>
      )}

      {/* Outcome summary — shown once recorded */}
      {outcome && (
        <Section>
          <Typography
            style={{
              fontWeight: 700,
              fontSize: rem(16),
              color: EPOCUS_TEXT_PRIMARY,
            }}
          >
            Outcome
          </Typography>
          <Box>
            <FieldLabel>Date Seen</FieldLabel>
            <FieldValue>{outcome.dateSeen}</FieldValue>
          </Box>
          <Box>
            <FieldLabel>Diagnosis Confirmed</FieldLabel>
            <FieldValue>
              {DIAGNOSIS_OPTIONS.find(
                (o) => o.value === outcome.diagnosisConfirmed,
              )?.label ?? outcome.diagnosisConfirmed}
            </FieldValue>
          </Box>
          <Box>
            <FieldLabel>Action Taken</FieldLabel>
            <FieldValue>
              {ACTION_OPTIONS.find((o) => o.value === outcome.actionTaken)
                ?.label ?? outcome.actionTaken}
            </FieldValue>
          </Box>
          {outcome.clinicalNotes && (
            <Box>
              <FieldLabel>Clinical Notes</FieldLabel>
              <FieldValue>{outcome.clinicalNotes}</FieldValue>
            </Box>
          )}
        </Section>
      )}
      {/* Hidden printable content — rendered only when isPrinting to avoid DOM bloat */}
      <Box display="none">
        {isPrinting && (
          <ReferralPrint ref={printRef} referral={referral} outcome={outcome} />
        )}
      </Box>
    </PageRoot>
  );
};
